import { ImageSet } from "../components/ImageUrlEditor";

// Keys used in localStorage
const KEYS = {
  HERO_IMAGE_URL: 'heroImageUrl',
  PRODUCT_IMAGE_URLS: 'productImageUrls',
  HERO_IMGIX_PARAMS: 'heroImgixParams',
  PRODUCT_IMGIX_PARAMS: 'productImgixParams',
  IMAGE_SETS: 'imageSets',
  CURRENT_IMAGE_SET_ID: 'currentImageSetId',
  LANGUAGE_PREFERENCE: 'languagePreference',
  getHeroParamsKey: (id: string) => `heroImgixParams_${id}`,
  getProductParamsKey: (id: string) => `productImgixParams_${id}`,
  getHeroEditorContentKey: (id: string) => `heroEditorContent_${id}`,
  getProductEditorContentKey: (id: string) => `productEditorContent_${id}`,
};

// Storage Service
export const storageService = {
  // Language Preference
  getLanguagePreference: (): string | null => {
    return localStorage.getItem(KEYS.LANGUAGE_PREFERENCE);
  },
  
  setLanguagePreference: (language: string): void => {
    localStorage.setItem(KEYS.LANGUAGE_PREFERENCE, language);
  },
  
  // Hero Image URL
  getHeroImageUrl: (): string | null => {
    return localStorage.getItem(KEYS.HERO_IMAGE_URL);
  },
  
  setHeroImageUrl: (url: string): void => {
    localStorage.setItem(KEYS.HERO_IMAGE_URL, url);
  },
  
  // Product Image URLs
  getProductImageUrls: (): string[] | null => {
    const urls = localStorage.getItem(KEYS.PRODUCT_IMAGE_URLS);
    return urls ? JSON.parse(urls) : null;
  },
  
  setProductImageUrls: (urls: string[]): void => {
    localStorage.setItem(KEYS.PRODUCT_IMAGE_URLS, JSON.stringify(urls));
  },
  
  // Global Hero Parameters
  getHeroImgixParams: (): Record<string, any> | null => {
    const params = localStorage.getItem(KEYS.HERO_IMGIX_PARAMS);
    return params ? JSON.parse(params) : null;
  },
  
  setHeroImgixParams: (params: Record<string, any>): void => {
    localStorage.setItem(KEYS.HERO_IMGIX_PARAMS, JSON.stringify(params));
  },
  
  // Global Product Parameters
  getProductImgixParams: (): Record<string, any> | null => {
    const params = localStorage.getItem(KEYS.PRODUCT_IMGIX_PARAMS);
    return params ? JSON.parse(params) : null;
  },
  
  setProductImgixParams: (params: Record<string, any>): void => {
    localStorage.setItem(KEYS.PRODUCT_IMGIX_PARAMS, JSON.stringify(params));
  },
  
  // Image Sets
  getImageSets: (): ImageSet[] => {
    const sets = localStorage.getItem(KEYS.IMAGE_SETS);
    return sets ? JSON.parse(sets) : [];
  },
  
  setImageSets: (imageSets: ImageSet[]): void => {
    localStorage.setItem(KEYS.IMAGE_SETS, JSON.stringify(imageSets));
  },
  
  // Current Image Set ID
  getCurrentImageSetId: (): string | null => {
    return localStorage.getItem(KEYS.CURRENT_IMAGE_SET_ID);
  },
  
  setCurrentImageSetId: (id: string | null): void => {
    if (id) {
      localStorage.setItem(KEYS.CURRENT_IMAGE_SET_ID, id);
    } else {
      localStorage.removeItem(KEYS.CURRENT_IMAGE_SET_ID);
    }
  },
  
  // Set-specific Hero Parameters
  getHeroParamsForSet: (setId: string): Record<string, any> | null => {
    const params = localStorage.getItem(KEYS.getHeroParamsKey(setId));
    return params ? JSON.parse(params) : null;
  },
  
  setHeroParamsForSet: (setId: string, params: Record<string, any>): void => {
    localStorage.setItem(KEYS.getHeroParamsKey(setId), JSON.stringify(params));
  },
  
  // Set-specific Product Parameters
  getProductParamsForSet: (setId: string): Record<string, any> | null => {
    const params = localStorage.getItem(KEYS.getProductParamsKey(setId));
    return params ? JSON.parse(params) : null;
  },
  
  setProductParamsForSet: (setId: string, params: Record<string, any>): void => {
    localStorage.setItem(KEYS.getProductParamsKey(setId), JSON.stringify(params));
  },
  
  // Set-specific Hero Editor Content
  getHeroEditorContentForSet: (setId: string): string | null => {
    return localStorage.getItem(KEYS.getHeroEditorContentKey(setId));
  },
  
  setHeroEditorContentForSet: (setId: string, content: string): void => {
    localStorage.setItem(KEYS.getHeroEditorContentKey(setId), content);
  },
  
  // Set-specific Product Editor Content
  getProductEditorContentForSet: (setId: string): string | null => {
    return localStorage.getItem(KEYS.getProductEditorContentKey(setId));
  },
  
  setProductEditorContentForSet: (setId: string, content: string): void => {
    localStorage.setItem(KEYS.getProductEditorContentKey(setId), content);
  },
  
  // Delete all data for a specific image set
  deleteImageSetData: (setId: string): void => {
    localStorage.removeItem(KEYS.getHeroParamsKey(setId));
    localStorage.removeItem(KEYS.getProductParamsKey(setId));
    localStorage.removeItem(KEYS.getHeroEditorContentKey(setId));
    localStorage.removeItem(KEYS.getProductEditorContentKey(setId));
  },
  
  // Clear all stored data
  clearAll: (): void => {
    localStorage.clear();
  }
};

export default storageService;